<template>
    <div>
        <kgr-loading v-if="loading" />
        <kgr-form-scaffold
            v-else
            :with-primary-buttons="isEditable"
            :save-button-disabled="saveButtonDisabled"
            :with-delete-button="false"
            @backBtnClicked="redirectToOverview"
            @primaryBtnClicked="save"
        >
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
                <fieldset class="kgr-grid__cell">
                    <legend
                        v-t="'Zugang zu Clubity'"
                        class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
                    ></legend>
                    <kgr-club-employee-login-form
                        v-if="clubEmployee"
                        :club-employee="clubEmployee"
                        :error="clubEmployeeError"
                    />
                </fieldset>

                <fieldset class="kgr-grid__cell">
                    <legend
                        v-t="'Wähle eine Rolle für den Mitarbeiter'"
                        class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
                    ></legend>
                    <kgr-form-row
                        :error="clubRoleError"
                        field-name="clubRoles"
                        required
                    >
                        <kgr-club-role-select
                            :options="clubRoles"
                            :value="selectedClubRoles"
                            display="displayName"
                            @post-input="inputClubRoles"
                        />
                    </kgr-form-row>
                    <div
                        class="kgr-inputGroup__text"
                        v-html="$t('clubEmployee.clubRole.hint', {url: clubRolesPdfPath})"
                    ></div>
                </fieldset>
            </div>
        </kgr-form-scaffold>
        <kgr-generic-modal
            :value="showDialog"
            :headline="$t('clubRoles.custom.admin.offering.modal.title')"
            @post-input="cancelDialog()"
        >
            <div>{{ $t('clubRoles.custom.admin.offering.modal.text') }}</div>
            <template #footer>
                <kgr-button
                    class="kgr-col"
                    :value="$t('Ok')"
                    @click="closeDialog()"
                />
                <kgr-button
                    type="grey"
                    :value="$t('cancel')"
                    @click="cancelDialog()"
                />
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import Button from '../../components/common/form/button/KgrButton.vue';
import ClubEmployeeLoginForm from '../../components/employees/ClubEmployeeLoginForm';
import ClubEmployeeRepository from '../../js/repository/ClubEmployeeRepository';
import ClubRoleRepository from '../../js/repository/ClubRoleRepository';
import ClubRoleSelect from '../../components/common/form/select/ClubRoleSelect.vue';
import Error from '../../js/model/Error';
import FormRow from '../../components/common/form/FormRow';
import FormScaffold from '../../components/common/form/FormScaffold';
import GenericModal from '../../components/common/modal/GenericModal.vue';
import Loading from '../../components/common/Loading';

export default {
    components: {
        'kgr-button': Button,
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-club-role-select': ClubRoleSelect,
        'kgr-loading': Loading,
        'kgr-club-employee-login-form': ClubEmployeeLoginForm,
        'kgr-generic-modal': GenericModal,
    },
    props: {
        overviewUrl: {
            type: String,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        employeeId: {
            type: Number,
            required: true,
        },
        clubRoleIds: {
            type: Array,
            required: false,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        message: {
            type: String,
            required: false,
            default: null,
        },
        clubRolesPdfPath: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            clubRoles: [],
            clubRolesLoading: false,
            clubEmployeeLoading: false,
            clubRoleError: new Error(),
            clubEmployeeError: new Error(),
            selectedClubRoles: [],
            clubEmployee: null,
            showDialog: false,
        }
    },
    computed: {
        loading() {
            return this.clubRolesLoading || this.clubEmployeeLoading;
        },
        saveButtonDisabled() {
            return this.selectedClubRoles.length === 0;
        }
    },
    mounted() {
        this.loadClubRoles();
        this.loadClubEmployee();
        if (this.message) {
            this.$notify.success(this.message);
        }
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        loadClubRoles() {
            this.clubRolesLoading = true
            ClubRoleRepository.findByClub(this.clubId).then(response => {
                this.clubRoles = response
                    .map(clubRole => this.withDisplayName(clubRole))
                    .filter(clubRole => this.isClubRoleVisible(clubRole));
                this.selectedClubRoles = this.clubRoles.filter(clubRole => this.clubRoleIds.includes(clubRole.id))
                this.clubRolesLoading = false;
            });
        },
        isClubRoleVisible(clubRole) {
            // An assigned role must stay listed even when hidden, otherwise saving would silently remove it.
            if (clubRole.custom || this.clubRoleIds.includes(clubRole.id)) {
                return true;
            }

            return !(window.kgr.hiddenFeatures || []).includes('clubRole.' + clubRole.name);
        },
        loadClubEmployee() {
            this.clubEmployeeLoading = true;
            ClubEmployeeRepository.getLoginData(this.employeeId)
                .then(clubEmployee => {
                    this.clubEmployee = clubEmployee;
                    this.clubEmployeeLoading = false;
                });
        },
        withDisplayName(clubRole) {
            clubRole.displayName = clubRole.custom ? clubRole.name : this.$t('clubRole.' + clubRole.name);
            return clubRole;
        },
        isOfferingAdminIncluded(roles) {
            return roles.some(role => role.displayName === 'Angebots-Admin');
        },
        inputClubRoles(clubRoles) {
            this.selectedClubRoles = clubRoles;
            this.showDialog = this.isOfferingAdminIncluded(this.selectedClubRoles);
        },
        save() {
            this.clubRolesLoading = true
            this.clubEmployeeLoading = true
            ClubRoleRepository.update(this.employeeId, { clubRoles: this.selectedClubRoles.map(r => r.id) })
                .then(() => this.clubRoleError = new Error())
                .catch(error => this.clubRoleError = error)
                .finally(() => {
                    this.clubRolesLoading = false;
                    this.checkSave();
                });
            ClubEmployeeRepository.updateLoginData(this.clubEmployee)
                .then(() => this.clubEmployeeError = new Error())
                .catch(error => this.clubEmployeeError = error)
                .finally(() => {
                    this.clubEmployeeLoading = false;
                    this.checkSave();
                });
        },
        checkSave() {
            if (this.clubRolesLoading || this.clubEmployeeLoading) {
                return;
            }
            if (this.clubRoleError.key === undefined && this.clubEmployeeError.key === undefined) {
                this.$notify.success('saveChanges.success');
            } else {
                this.$notify.error('saveChanges.error');
            }
        },
        closeDialog() {
            this.showDialog = false;
        },
        cancelDialog() {
            this.selectedClubRoles = this.selectedClubRoles.filter(role => role.displayName !== 'Kursmodul Admin');
            this.closeDialog();
        }
    }
}
</script>
<style scoped lang="scss" rel="stylesheet/scss"></style>
